import { useTimeoutFn, watchImmediate } from "@vueuse/core";
import { computed } from "vue";
import { toBoolean, toNumber } from "#ce/utils/props";

const AUTOREMOVE_DELAY_MS = 3000;
const DISMISSING_CLASS = "--is-dismissing";
const DISMISS_ANIMATION = "sp-alert-dismiss";
// Added to the animation's own end time, so the escape never truncates the rendered fade.
const DISMISS_TIMEOUT_MARGIN_MS = 250;
// A frame is ~16ms. Browsers pause rAF in background tabs, so the wait for one is bounded.
const FRAME_TIMEOUT_MS = 100;

function toFinished(animation) {
  return animation.finished;
}

// The host carries whatever animations a consumer has put on it; waiting on those would
// strand the alert behind an animation that never finishes.
function dismissAnimations(host) {
  const running = host?.getAnimations?.() ?? [];

  return running.filter((animation) => animation.animationName === DISMISS_ANIMATION);
}

function after(delay) {
  return new Promise((resolve) => setTimeout(resolve, delay));
}

// A paused animation reports an end time it will never reach, which is what bounds the wait.
function dismissDeadline(animations) {
  const endTimes = animations.map((animation) => Number(animation.effect?.getComputedTiming?.().endTime) || 0);

  return Math.max(0, ...endTimes) + DISMISS_TIMEOUT_MARGIN_MS;
}

function nextFrame() {
  const frame = new Promise((resolve) => requestAnimationFrame(resolve));

  return Promise.race([frame, after(FRAME_TIMEOUT_MS)]);
}

/**
 * Runs the alert's dismiss animation, then hands back to the caller to remove it, and schedules
 * that dismissal on a timer while `autoremove` is set. Reads `autoremove` and `autoremoveDelay`
 * off the alert's props; a non-numeric delay falls back to three seconds.
 *
 * @param {Object} props - The alert's props.
 * @param {HTMLElement|null} host - The custom element host, which carries the animation.
 * @param {Function} onDismissed - Called once the animation has finished; removes the alert.
 * @returns {{ dismiss: Function }} `dismiss` runs the animation and then `onDismissed`.
 */
export function useAlertDismissal(props, host, onDismissed) {
  async function dismiss() {
    // Dismissal has started, so a pending autoremove has nothing left to do.
    stop();
    host?.classList.add(DISMISSING_CLASS);
    // A just-applied class has not started animating yet, so getAnimations() would be empty.
    // Where no frame arrives the list stays empty and dismissal falls straight through.
    await nextFrame();

    const animations = dismissAnimations(host);
    const finished = Promise.allSettled(animations.map(toFinished));

    // A paused or stalled dismiss animation must not keep the alert on screen for good.
    await Promise.race([finished, after(dismissDeadline(animations))]);
    onDismissed();
  }

  // Number("") is 0 rather than NaN, so a bare autoremove-delay would dismiss at once.
  const delay = computed(() => {
    const requested = props.autoremoveDelay;

    if (requested === "" || requested === null || requested === undefined) {
      return AUTOREMOVE_DELAY_MS;
    }

    return toNumber(requested, AUTOREMOVE_DELAY_MS);
  });
  const { start, stop } = useTimeoutFn(dismiss, delay, { immediate: false });

  function autoremoveSource() {
    return [toBoolean(props.autoremove), delay.value];
  }

  // Restarts on a delay change too, so the alert always waits the delay currently asked for.
  function scheduleAutoremove([autoremove]) {
    stop();

    if (autoremove) {
      start();
    }
  }

  watchImmediate(autoremoveSource, scheduleAutoremove);

  return { dismiss };
}
